import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X as XIcon, PencilSimple, FloppyDisk, FilePdf, ArrowRight, User as UserIcon, Package, MapPin, Calendar, Note as NoteIcon, Signature as SigIcon, Trash, Plus, PaperPlaneTilt } from "@phosphor-icons/react";
import SearchableSelect from "@/components/SearchableSelect";
import { effectiveSupplier } from "@/components/MovementListTable";
import { useAuth } from "@/context/AuthContext";

const TYPE_LABEL = {
  entry: "Entrata", exit: "Uscita/DDT", return: "Reso",
  transfer: "Trasferimento", scarico_commessa: "Scarico Commessa",
};

/**
 * Full-detail view of a movement, with all serial numbers expanded and (for the
 * creator or admin) an inline edit form for safe metadata fields.
 */
export default function MovementDetailModal({ open, onClose, movementId, onUpdated, customers = [], commesse = [], products = [], warehouses = [] }) {
  const { user } = useAuth();
  const [m, setM] = useState(null);
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);
  const [form, setForm] = useState({});
  // Add-product-to-group inline form state
  const [addProductId, setAddProductId] = useState("");
  const [addQuantity, setAddQuantity] = useState(1);
  const [addSerialsText, setAddSerialsText] = useState("");
  const [addingProduct, setAddingProduct] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Options for product dropdowns (edit form + add-to-group form)
  const productOpts = useMemo(
    () => (products || []).map((p) => ({
      value: p.id,
      label: `${p.sku} — ${p.name}`,
      sublabel: p.is_serialized === false ? "Quantità (bulk)" : "Seriale",
      keywords: [p.sku, p.name, p.manufacturer_code].filter(Boolean),
    })),
    [products]
  );

  const reloadGroup = async (mid) => {
    try {
      const gr = await api.get(`/movements/${mid}/group`);
      setGroup(gr.data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!open || !movementId) return;
    setLoading(true); setEditing(false); setM(null); setGroup(null);
    setAddProductId(""); setAddQuantity(1); setAddSerialsText("");
    api.get(`/movements/${movementId}`)
      .then((r) => { setM(r.data); setForm({
        reference: r.data.reference || "",
        ddt_number: r.data.ddt_number || "",
        notes: r.data.notes || "",
        customer_id: r.data.customer_id || "",
        commessa_id: r.data.commessa_id || "",
        product_id: r.data.product_id || "",
        quantity: r.data.quantity || 1,
        serial_numbers: (r.data.serial_numbers || []).join(", "),
        from_warehouse_id: r.data.from_warehouse_id || "",
        to_warehouse_id: r.data.to_warehouse_id || "",
      });
      // If the movement is part of a group, load all siblings
      if (r.data.movement_group_id) {
        reloadGroup(movementId);
      }
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [open, movementId]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        reference: form.reference,
        ddt_number: form.ddt_number,
        notes: form.notes,
        customer_id: form.customer_id || null,
        commessa_id: form.commessa_id || null,
        product_id: form.product_id,
        quantity: parseInt(form.quantity) || 1,
        from_warehouse_id: form.from_warehouse_id || null,
        to_warehouse_id: form.to_warehouse_id || null,
      };
      // Split "serials" textarea only if the movement is serialized
      const selectedProd = products.find((p) => p.id === form.product_id);
      if (selectedProd && selectedProd.is_serialized !== false) {
        payload.serial_numbers = form.serial_numbers.split(/[\s,;\n]+/).map((s) => s.trim()).filter(Boolean);
      }
      const { data } = await api.put(`/movements/${movementId}`, payload);
      setM(data);
      setEditing(false);
      toast.success("Movimento aggiornato");
      onUpdated && onUpdated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const resendDdtEmail = async () => {
    if (!m) return;
    setResending(true);
    try {
      const { data } = await api.post(`/movements/${m.id}/resend-ddt-email`);
      const n = data?.notification || {};
      if (n.sent) {
        const extra = [n.cc?.length ? ` · ${n.cc.length} cc` : "", n.bcc?.length ? ` · ${n.bcc.length} bcc` : ""].join("");
        toast.success(`Email DDT inviata a ${(n.to || []).join(", ") || "destinatario configurato"}${extra}`);
      } else if (n.skipped) {
        toast.warning(`Invio saltato: ${n.skipped}`);
      } else {
        toast.error(n.error || "Invio fallito");
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setResending(false); }
  };

  const deleteGroupItem = async (itemId, itemLabel) => {
    if (!window.confirm(`Rimuovere "${itemLabel}" dal movimento?\nLa giacenza verrà ripristinata automaticamente.`)) return;
    setDeletingId(itemId);
    try {
      await api.delete(`/movements/${itemId}`);
      toast.success("Prodotto rimosso dal movimento");
      // If we deleted the currently-viewed row, close after refresh
      if (itemId === movementId) {
        onUpdated && onUpdated();
        onClose();
        return;
      }
      await reloadGroup(movementId);
      onUpdated && onUpdated();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // 409 = last item of a group → prompt user to cancel the whole movement
      if (e?.response?.status === 409 && detail?.code === "group_would_be_empty") {
        if (window.confirm(detail.message + "\n\nProcedere?")) {
          try {
            await api.delete(`/movements/${itemId}`, { params: { force: true } });
            toast.success("Movimento annullato");
            onUpdated && onUpdated();
            onClose();
            return;
          } catch (e2) { toast.error(formatApiError(e2)); }
        }
      } else {
        toast.error(formatApiError(e));
      }
    } finally { setDeletingId(null); }
  };

  const addProductToGroup = async () => {
    if (!addProductId) { toast.error("Seleziona un prodotto"); return; }
    const prod = products.find((p) => p.id === addProductId);
    const isBulkProduct = prod && prod.is_serialized === false;
    let payload = { product_id: addProductId };
    if (isBulkProduct) {
      const q = parseInt(addQuantity, 10);
      if (!q || q <= 0) { toast.error("Quantità non valida"); return; }
      payload.quantity = q;
      payload.serial_numbers = null;
    } else {
      const serials = addSerialsText.split(/[\s,;\n]+/).map((s) => s.trim()).filter(Boolean);
      if (!serials.length) { toast.error("Inserisci almeno un seriale"); return; }
      payload.serial_numbers = serials;
      payload.quantity = serials.length;
    }
    setAddingProduct(true);
    try {
      const gid = m.movement_group_id || group?.group_id;
      if (!gid) { toast.error("Movimento non raggruppato"); return; }
      const { data } = await api.post(`/movements/group/${gid}/add`, { items: [payload] });
      if (data.errors && data.errors.length) {
        toast.warning(data.errors.join("\n"));
      } else {
        toast.success("Prodotto aggiunto al movimento");
      }
      setAddProductId(""); setAddQuantity(1); setAddSerialsText("");
      await reloadGroup(movementId);
      onUpdated && onUpdated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAddingProduct(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-2 md:p-4" data-testid="mov-detail-modal">
      <div className="bg-white border border-neutral-200 shadow-xl w-full max-w-3xl max-h-[95vh] overflow-y-auto">
        <div className="p-4 border-b border-neutral-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="label-eyebrow text-neutral-500">Movimento</div>
            <h3 className="font-display font-bold text-lg tracking-tight">
              {m ? `${TYPE_LABEL[m.type] || m.type}${m.ddt_number ? ` · ${m.ddt_number}` : ""}` : "Caricamento..."}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            {m && m.can_edit && !editing && (
              <button data-testid="mov-detail-edit" onClick={() => setEditing(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white text-xs uppercase tracking-wider transition-colors">
                <PencilSimple size={13} weight="bold" /> Modifica
              </button>
            )}
            <button onClick={onClose} data-testid="mov-detail-close" className="p-1.5 hover:bg-neutral-100 transition-colors">
              <XIcon size={18} />
            </button>
          </div>
        </div>

        {loading || !m ? (
          <div className="p-8 text-center text-neutral-500">Caricamento...</div>
        ) : (
          <div className="p-4 md:p-6 space-y-4">
            {/* Group siblings (multi-product movement) */}
            {group && group.items && group.items.length > 0 && m.movement_group_id && (
              <div className="border border-[#002FA7]/30 bg-[#002FA7]/5 p-3">
                <div className="label-eyebrow mb-2 text-[#002FA7]">
                  Movimento raggruppato — {group.items.length} prodott{group.items.length === 1 ? "o" : "i"}
                </div>
                <div className="space-y-2">
                  {group.items.map((it) => (
                    <div key={it.id} data-testid={`mov-group-item-${it.id}`}
                      className={`p-2 text-sm border ${it.id === m.id ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white border-[#002FA7]/20"}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate">
                          <b>{it.product_sku}</b> — {it.product_name}
                        </span>
                        <div className="flex items-center gap-2 whitespace-nowrap">
                          <span className="font-mono text-xs">
                            {it.quantity} {it.unit_of_measure || "pz"}
                          </span>
                          {editing && m.can_edit && (
                            <button
                              type="button"
                              data-testid={`mov-group-item-delete-${it.id}`}
                              onClick={() => deleteGroupItem(it.id, `${it.product_sku} — ${it.product_name}`)}
                              disabled={deletingId === it.id}
                              title="Rimuovi dal movimento"
                              className={`inline-flex items-center gap-1 px-2 py-0.5 border text-[11px] uppercase tracking-wider transition-colors ${
                                it.id === m.id
                                  ? "border-white/60 text-white hover:bg-white hover:text-red-600"
                                  : "border-red-300 text-red-600 hover:bg-red-50"
                              } disabled:opacity-40`}
                            >
                              <Trash size={11} weight="bold" />
                              {deletingId === it.id ? "..." : "Rimuovi"}
                            </button>
                          )}
                        </div>
                      </div>
                      {(it.serial_numbers || []).length > 0 && (
                        <div className={`mt-1.5 pt-1.5 border-t ${it.id === m.id ? "border-white/30" : "border-neutral-200"}`}>
                          <div className={`text-[10px] uppercase tracking-wider mb-1 ${it.id === m.id ? "text-white/70" : "text-neutral-500"}`}>
                            Seriali ({(it.serial_numbers || []).length})
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {(it.serial_numbers || []).map((s) => (
                              <span key={s} data-testid={`mov-group-serial-${it.id}-${s}`}
                                className={`font-mono text-[11px] px-1.5 py-0.5 ${it.id === m.id ? "bg-white/15" : "bg-neutral-100 border border-neutral-200"}`}>
                                {s}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Add-product-to-group inline form (only while editing) */}
                {editing && m.can_edit && (
                  <div className="mt-3 pt-3 border-t border-[#002FA7]/30 space-y-2">
                    <div className="label-eyebrow text-[#002FA7]">Aggiungi un prodotto al gruppo</div>
                    <SearchableSelect
                      testid="mov-group-add-product"
                      value={addProductId}
                      options={productOpts}
                      onChange={(v) => { setAddProductId(v); setAddQuantity(1); setAddSerialsText(""); }}
                      placeholder="Cerca e seleziona prodotto…"
                    />
                    {addProductId && (
                      <>
                        {products.find((p) => p.id === addProductId)?.is_serialized === false ? (
                          <input
                            data-testid="mov-group-add-quantity"
                            type="number" min="1" value={addQuantity}
                            onChange={(e) => setAddQuantity(e.target.value)}
                            placeholder="Quantità"
                            className="w-full px-3 py-2 border border-neutral-300 text-sm font-mono bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                          />
                        ) : (
                          <textarea
                            data-testid="mov-group-add-serials"
                            rows={2} value={addSerialsText}
                            onChange={(e) => setAddSerialsText(e.target.value)}
                            placeholder="SN-001, SN-002..."
                            className="w-full px-3 py-2 border border-neutral-300 text-sm font-mono bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                          />
                        )}
                      </>
                    )}
                    <button
                      type="button"
                      data-testid="mov-group-add-submit"
                      disabled={!addProductId || addingProduct}
                      onClick={addProductToGroup}
                      className="w-full flex items-center justify-center gap-1.5 px-3 py-2 border-2 border-dashed border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7]/5 text-xs uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Plus size={13} weight="bold" />
                      {addingProduct ? "Aggiunta..." : "Aggiungi al movimento"}
                    </button>
                  </div>
                )}

                <div className="text-[11px] text-neutral-600 mt-2">
                  La firma si applica automaticamente a tutti i prodotti del gruppo.
                </div>
              </div>
            )}

            {/* Metadata grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <Field icon={Calendar} label="Data" value={new Date(m.created_at).toLocaleString("it-IT")} />
              <Field icon={UserIcon} label="Utente" value={m.created_by_name || m.created_by || "—"} />
              {editing ? (
                <label className="block md:col-span-2">
                  <span className="label-eyebrow block mb-1">Prodotto *</span>
                  <SearchableSelect
                    testid="mov-edit-product"
                    value={form.product_id}
                    options={productOpts}
                    onChange={(v) => setForm({ ...form, product_id: v })}
                    placeholder="Cerca e seleziona prodotto…"
                  />
                </label>
              ) : (
                <>
                  <Field icon={Package} label="Prodotto" value={`${m.product_sku} — ${m.product_name}`} />
                  <Field icon={Package} label="Cod. Produttore" value={m.product_manufacturer_code || "—"} />
                </>
              )}
              {editing ? (
                <>
                  <label className="block">
                    <span className="label-eyebrow block mb-1">Da magazzino</span>
                    <select value={form.from_warehouse_id} onChange={(e) => setForm({ ...form, from_warehouse_id: e.target.value })}
                      className="w-full px-3 py-2 border border-neutral-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
                      <option value="">— Nessuno —</option>
                      {warehouses.map((w) => (<option key={w.id} value={w.id}>{w.name}</option>))}
                    </select>
                  </label>
                  <label className="block">
                    <span className="label-eyebrow block mb-1">A magazzino</span>
                    <select value={form.to_warehouse_id} onChange={(e) => setForm({ ...form, to_warehouse_id: e.target.value })}
                      className="w-full px-3 py-2 border border-neutral-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
                      <option value="">— Nessuno —</option>
                      {warehouses.map((w) => (<option key={w.id} value={w.id}>{w.name}</option>))}
                    </select>
                  </label>
                </>
              ) : (
                <>
                  <Field icon={MapPin} label="Da magazzino" value={m.from_warehouse_name || "—"} />
                  <Field icon={MapPin} label="A magazzino" value={m.to_warehouse_name || "—"} />
                </>
              )}
              {editing ? (
                <>
                  <label className="block">
                    <span className="label-eyebrow block mb-1">Quantità *</span>
                    <input type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                      className="w-full px-3 py-2 border border-neutral-300 text-sm font-mono bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
                  </label>
                  <Field label="Fornitore" value={effectiveSupplier(m) || "—"} />
                </>
              ) : (
                <>
                  <Field label="Quantità" value={`${m.quantity} ${m.unit_of_measure || "pz"}`} />
                  <Field label="Fornitore" value={effectiveSupplier(m) || "—"} />
                </>
              )}
            </div>

            {/* Serial numbers — editable textarea if the (possibly new) product is serialized */}
            {editing && products.find((p) => p.id === form.product_id)?.is_serialized !== false && (
              <div>
                <div className="label-eyebrow mb-1 text-[#002FA7]">Numeri seriali (uno per riga o separati da virgola)</div>
                <textarea value={form.serial_numbers} onChange={(e) => setForm({ ...form, serial_numbers: e.target.value })}
                  rows={3} placeholder="SN-001&#10;SN-002" className="w-full px-3 py-2 border border-neutral-300 text-sm font-mono bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              </div>
            )}
            {!editing && Array.isArray(m.serial_numbers) && m.serial_numbers.length > 0 && (
              <div>
                <div className="label-eyebrow mb-2 text-[#002FA7]">Numeri seriali movimentati ({m.serial_numbers.length})</div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1.5 max-h-56 overflow-y-auto border border-neutral-200 p-2 bg-neutral-50">
                  {m.serial_numbers.map((s) => (
                    <span key={s} data-testid={`mov-serial-${s}`} className="font-mono text-xs bg-white border border-neutral-200 px-2 py-1 truncate" title={s}>{s}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Editable metadata block */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <EditableField label="Riferimento" editing={editing} value={form.reference}
                onChange={(v) => setForm({ ...form, reference: v })} display={m.reference || "—"} />
              <EditableField label="Numero DDT" editing={editing} value={form.ddt_number}
                onChange={(v) => setForm({ ...form, ddt_number: v })} display={m.ddt_number || "—"} />
              {editing ? (
                <label className="block">
                  <span className="label-eyebrow block mb-1">Cliente destinatario</span>
                  <select value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
                    <option value="">— Nessuno —</option>
                    {customers.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                  </select>
                </label>
              ) : (
                <Field label="Cliente destinatario" value={m.customer_name || "—"} />
              )}
              {editing ? (
                <label className="block">
                  <span className="label-eyebrow block mb-1">Commessa</span>
                  <select value={form.commessa_id} onChange={(e) => setForm({ ...form, commessa_id: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
                    <option value="">— Nessuna —</option>
                    {commesse.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                  </select>
                </label>
              ) : (
                <Field label="Commessa" value={m.commessa_name || "—"} />
              )}
            </div>

            {/* Note (full editable text area) */}
            <div>
              <div className="flex items-center gap-1.5 mb-1">
                <NoteIcon size={14} weight="bold" className="text-neutral-500" />
                <span className="label-eyebrow">Note</span>
              </div>
              {editing ? (
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={3} className="w-full px-3 py-2 border border-neutral-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              ) : (
                <div className="p-2 border border-neutral-200 bg-neutral-50 text-sm text-neutral-700 whitespace-pre-wrap min-h-[40px]">{m.notes || <span className="text-neutral-400">Nessuna nota</span>}</div>
              )}
            </div>

            {/* Signatures status */}
            {(m.signatures?.incaricato || m.signatures?.destinatario) && (
              <div className="border border-emerald-200 bg-emerald-50 p-3">
                <div className="flex items-center gap-2 text-xs text-emerald-800 font-semibold uppercase tracking-wider mb-1">
                  <SigIcon size={13} weight="bold" /> Firme acquisite
                </div>
                <div className="text-xs text-emerald-700">
                  {m.signatures?.incaricato && <div>Incaricato: {m.signatures.incaricato.signer_name || "—"} · {m.signatures.incaricato.signed_at ? new Date(m.signatures.incaricato.signed_at).toLocaleString("it-IT") : ""}</div>}
                  {m.signatures?.destinatario && <div>Destinatario: {m.signatures.destinatario.signer_name || "—"} · {m.signatures.destinatario.signed_at ? new Date(m.signatures.destinatario.signed_at).toLocaleString("it-IT") : ""}</div>}
                </div>
              </div>
            )}

            {editing && (
              <div className="border-t border-neutral-200 pt-3 flex items-center justify-between text-xs text-amber-700 bg-amber-50 p-2 border border-amber-200">
                <span>⚠ Modificando prodotto/quantità/seriali/magazzini la giacenza verrà ricalcolata automaticamente.</span>
              </div>
            )}

            <div className="flex items-center justify-between gap-2 border-t border-neutral-200 pt-3">
              {m.type === "exit" && m.ddt_number ? (
                <Link to="/ddt" onClick={onClose} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#002FA7] hover:underline">
                  Vedi in DDT <ArrowRight size={12} weight="bold" />
                </Link>
              ) : <span />}
              <div className="flex items-center gap-2">
                <button
                  data-testid="mov-detail-download-pdf"
                  onClick={async () => {
                    try {
                      const res = await api.get(`/movements/${m.id}/pdf`, { responseType: "blob" });
                      const url = window.URL.createObjectURL(res.data);
                      const a = document.createElement("a"); a.href = url; a.download = `${m.ddt_number || "movimento-" + m.id.slice(0,8)}.pdf`; a.click();
                      window.URL.revokeObjectURL(url);
                    } catch (e) { toast.error(formatApiError(e)); }
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-neutral-300 hover:bg-neutral-50 text-xs uppercase tracking-wider"
                >
                  <FilePdf size={13} weight="bold" /> PDF
                </button>
                {m.type === "exit" && m.ddt_number && (user?.role === "admin" || user?.role === "gestore") && (
                  <button
                    data-testid="mov-detail-resend-ddt-email"
                    type="button"
                    disabled={resending}
                    onClick={resendDdtEmail}
                    title="Reinvia l'email 'DDT emesso' al cliente (template associato)"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7]/5 disabled:opacity-40 text-xs uppercase tracking-wider"
                  >
                    <PaperPlaneTilt size={13} weight="bold" /> {resending ? "Invio…" : "Reinvia email"}
                  </button>
                )}
                {editing && (
                  <>
                    <button onClick={() => { setEditing(false); setForm({
                      reference: m.reference || "", ddt_number: m.ddt_number || "",
                      notes: m.notes || "", customer_id: m.customer_id || "", commessa_id: m.commessa_id || "",
                    }); }} className="px-3 py-1.5 border border-neutral-300 text-xs uppercase tracking-wider hover:bg-neutral-50">
                      Annulla
                    </button>
                    <button data-testid="mov-detail-save" onClick={save} disabled={saving}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#002FA7] hover:bg-[#002480] disabled:opacity-40 text-white text-xs uppercase tracking-wider">
                      <FloppyDisk size={13} weight="bold" /> {saving ? "Salvataggio..." : "Salva"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-0.5">
        {Icon && <Icon size={12} weight="bold" className="text-neutral-500" />}
        <span className="label-eyebrow">{label}</span>
      </div>
      <div className="font-mono text-sm text-neutral-900">{value}</div>
    </div>
  );
}

function EditableField({ label, editing, value, onChange, display }) {
  if (!editing) return <Field label={label} value={display} />;
  return (
    <label className="block">
      <span className="label-eyebrow block mb-1">{label}</span>
      <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-neutral-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
    </label>
  );
}
